import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { Fragment, useCallback, useEffect, useState } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useLoaderData, useFetcher, useRouteLoaderData } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"

import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"

import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import { getOrganisations, getProjectsByOrganisation } from "@/utils/projects"

import { hasPermission } from "@/machines/Authmachine"

import type { RootLoader } from "@/routes/root"
import type { Tag } from "@/utils/types"

const routeId = "tags-builder"
const sessionKey = "tag-filter-storage"

const tagRestrictionOptions = [
  { value: "freeTextMultiple", label: "Free Text (Multiple allowed)" },
  { value: "freeTextSingle", label: "Free Text (Singular)" },
  { value: "fixedMultiple", label: "Fixed (Multiple allowed)" },
  { value: "fixedSingle", label: "Fixed (Singular)" },
  { value: "autoTag", label: "Auto Tag" },
  { value: "instanceAutoTag", label: "Instance Auto Tag" },
]

async function loader() {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const projects = getProjectsByOrganisation(loadedOrganisation)

  const loadedProject =
    filters.parsed?.project == null ? projects[0] : (projects.find((p) => p === filters.parsed?.project) ?? projects[0])

  const loadedTagType = filters.parsed?.tagType

  const [tags, tagTypes] = await Promise.all([
    fetchAPI<Tag[]>(
      `/api/UnityViewer/tag/${loadedOrganisation}/${loadedProject}/${loadedTagType == null ? "" : encodeURIComponent(loadedTagType)}`,
    ).then(handleResponse),
    fetchAPI<Tag[]>(`/api/UnityViewer/TagType/${loadedOrganisation}/${loadedProject}`).then(handleResponse),
  ])

  return { tags, tagTypes, loadedOrganisation, projects, loadedProject, loadedTagType }
}

async function action({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  let key: string | undefined
  let errorMessage: string | undefined

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "CREATE": {
      const tagType = formData.get("tagType") as string
      const name = formData.get("name") as string | null

      if (name == null || tagType == null) {
        errorMessage = "Missing Tag Name/Type"
        break
      }

      try {
        await fetchAPI(`/api/UnityViewer/tag/${organisation}/${project}`, {
          method: "POST",
          body: { name, tagType },
        }).then(throwIfNotOk)
        key = Math.random().toString(36)
      } catch (error) {
        console.error(error)
        errorMessage = "Error Adding Tag"
      }

      break
    }

    default:
      throw new Error("Unknown action")
  }

  return { action, error: errorMessage, key }
}

async function mainAction({ request }: { request: Request }) {
  const formData = await request.formData()
  const { action, name, tagType, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  let errorMessage: string | undefined

  switch (action) {
    case "DELETE": {
      if (name == null) {
        errorMessage = "Missing Tag Name"
        break
      }

      try {
        await fetchAPI(`/api/UnityViewer/tag/${organisation}/${project}/${encodeURIComponent(name)}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
      } catch (error) {
        console.error(error)
        errorMessage = "Error Deleting Tag"
      }
      break
    }
    case "EDIT": {
      if (name == null || tagType == null) {
        errorMessage = "Missing Tag Name/Type"
        break
      }

      const newName = formData.get("newName") as string | null

      try {
        await fetchAPI(`/api/UnityViewer/tag/${organisation}/${project}/${encodeURIComponent(name)}`, {
          method: "PUT",
          body: { newName: newName === name ? undefined : newName, tagType },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage = "Error Updating Tag"
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

export function Tags() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<typeof loader>(routeId)

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((data, reason) => {
    if (reason.type !== "field") return

    if (reason.name === "organisation") data.delete("project")
    if (reason.name === "organisation" || reason.name === "project") data.delete("tagType")
  }, [])

  return (
    <Fragment>
      {rootData != null && data != null && (
        <div className="flex items-center gap-4">
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
            <FiltersWrapper.Select
              name="tagType"
              label="Tag Type"
              placeholder="Select a Tag Type"
              defaultValue={data.loadedTagType}
              items={data.tagTypes.map((t) => t.name)}
            />
          </FiltersWrapper>
          <div className="flex-1" />
          <TagAddNew />
        </div>
      )}
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<typeof loader>()

  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead></TableHead>
            <TableHead className="text-left">Tag Name</TableHead>
            <TableHead className="text-left">Tag Type</TableHead>
            <TableHead className="text-left">Tag Restriction</TableHead>
            <TableHead>Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.tags.length > 0 ? (
            data.tags.map((element, index) => (
              <TableRow key={element.name} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell>{element.name}</TableCell>
                <TableCell>{element.tagType}</TableCell>
                <TableCell>
                  {tagRestrictionOptions.find((option) => option.value === element.tagRestriction)?.label ??
                    element.tagRestriction}
                </TableCell>
                <TableCell className="space-x-2">
                  <TagEdit {...element} />
                  <TagDelete {...element} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={12} className="py-4 text-center text-gray-500">
                No data found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function TagAddNew() {
  const fetcher = useFetcher<typeof action>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog key={fetcher.data?.key}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Tag</DialogTitle>
        </DialogHeader>
        <fetcher.Form method="POST" className="space-y-4">
          <input type="hidden" name="action" value="CREATE" />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          <input type="hidden" name="project" value={data.loadedProject} />
          <div className="flex-1" />
          <div>
            <label className="text-sm font-medium">Tag Name</label>
            <Input required type="text" name="name" placeholder="Tag Name" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Tag Type</label>
            <Select required name="tagType" defaultValue={data.loadedTagType}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select Tag Type" />
              </SelectTrigger>
              <SelectContent>
                {data.tagTypes.map((opt) => (
                  <SelectItem key={opt.name} value={opt.name}>
                    {opt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center justify-start gap-4">
            {fetcher.state !== "idle" ? (
              <span className="flex items-center justify-start gap-1 pr-4">
                <LoaderCircleIcon className="size-4 animate-spin" /> Loading
              </span>
            ) : (
              fetcher.data?.error != null && <p className="text-sm text-destructive">{fetcher.data.error}</p>
            )}
            <div className="flex-1" />
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button type="submit">Submit</Button>
          </div>
        </fetcher.Form>
      </DialogContent>
    </Dialog>
  )
}

function TagDelete(tags: Tag) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Tag">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start" className="max-w-sm">
        <p className="text-sm text-pretty">
          &quot;<span className="font-semibold">{tags.name}</span>&quot; will be deleted from the Tag List. Are you
          sure?
        </p>
        <TagDeleteForm {...tags} />
      </PopoverContent>
    </Popover>
  )
}

function TagDeleteForm({ name, tagType }: Tag) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />
      <input type="hidden" name="tagType" value={tagType} />
      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
        </span>
      ) : (
        fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

function TagEdit(tags: Tag) {
  const [open, setOpen] = useState(false)
  const closeDialog = useCallback(() => setOpen(false), [])

  const data = useRouteLoaderData<typeof loader>(routeId)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Tag">
          <PencilIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tag</DialogTitle>
        </DialogHeader>
        <TagEditForm {...tags} onSuccess={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function TagEditForm({ name, tagType, onSuccess }: Tag & { onSuccess: () => void }) {
  const fetcher = useFetcher<typeof mainAction>()
  const data = useRouteLoaderData<typeof loader>(routeId)

  useEffect(() => {
    if (fetcher.data?.action !== "EDIT" || fetcher.data.errorMessage != null) return
    onSuccess()
  }, [onSuccess, fetcher.data])

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="EDIT" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />
      <div className="flex-1" />
      <div>
        <label className="text-sm font-medium">Tag Name</label>
        <Input type="text" required defaultValue={name} name="newName" />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Tag Type</label>
        <Select required name="tagType" defaultValue={tagType}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select Tag Type" />
          </SelectTrigger>
          <SelectContent className="max-h-60 overflow-y-auto">
            {data.tagTypes.map((opt) => (
              <SelectItem key={opt.name} value={opt.name}>
                {opt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Loading
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit">Submit</Button>
      </div>
    </fetcher.Form>
  )
}

Tags.loader = loader
Tags.action = action
Tags.Main = Main
Tags.routeId = routeId

Main.action = mainAction
